import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withTracker } from 'meteor/react-meteor-data';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';
import { filter, isArray, isEmpty, isEqual, isNil, map } from 'lodash';

import Annotations from '../../../api/Annotations/Annotations';
import * as projectOps from '../../../api/Projects/project-operations';
import Projects from '../../../api/Projects/Projects';
import * as videoOps from '../../../api/Videos/video-operations';
import Videos from '../../../api/Videos/Videos';

import methodPromise from '../../../modules/method-promise-wrapper';
import handleErrorInClient from '../../../modules/handle-error-in-client';

import PageHeader from '../../atoms/PageHeader';
import TableCopyCell from '../../atoms/TableCopyCell';
import TableNameCell from '../../atoms/TableNameCell';
import Loading from '../../components/Loading';
import SEO from '../../components/SEO';
import ListModal from '../../components/ListModal';
import VideoVisualizerContainer from '../../containers/VideoVisualizerContainer';
import NotFound from '../NotFound';

const ITEMS_PER_PAGE = 10;

class PublicVideo extends Component {
  state = {
    showList: false,
    currentPage: 1
  };

  componentDidUpdate(prevProps) {
    const { projects } = this.props;
    const { projects: prevProjects } = prevProps;
    const { currentPage } = this.state;

    const skip = currentPage * ITEMS_PER_PAGE - ITEMS_PER_PAGE;
    const pageProjects = filter(
      projects,
      (projectItem, index) => index >= skip && index < skip + ITEMS_PER_PAGE
    );

    if (!isEqual(projects, prevProjects) && isEmpty(pageProjects) && currentPage > 1)
      this.changePage(currentPage - 1);
  }

  copyVideoToProject = async (videoId, projectId, projectName) => {
    let copyAnnotationsIds = [];
    let originVideoId = '';
    let destVideoId = '';
    try {
      const { history } = this.props;
      ({ originVideoId, destVideoId } = await methodPromise('videos.copy', videoId, projectId));
      copyAnnotationsIds = await methodPromise('annotations.copy', originVideoId, destVideoId);
      Bert.alert(`Item copied to ${projectName}!`, 'success');
      history.push(`/projects/${projectId}/videos/${destVideoId}`);
    } catch (error) {
      this.cancelCopy(error, destVideoId, copyAnnotationsIds);
    }
  };

  cancelCopy = async (copyError, copyVideoId, copyAnnotationsIds) => {
    try {
      if (isArray(copyAnnotationsIds) && !isNil(copyAnnotationsIds))
        await Promise.all(
          map(copyAnnotationsIds, copyAnnotationId =>
            isEmpty(copyAnnotationId)
              ? ''
              : methodPromise('annotations.hardDelete', copyAnnotationId)
          )
        );
      if (!isEmpty(copyVideoId)) await methodPromise('videos.hardDelete', copyVideoId);
      Bert.alert('Copy cancelled', 'danger');
      console.error(copyError);
    } catch (error) {
      Bert.alert('Deletion failed', 'danger');
      handleErrorInClient(error);
    }
  };

  closeProjectList = () => this.setState({ showList: false });

  openProjectList = () => this.setState({ showList: true });

  changePage = pageNumber => this.setState({ currentPage: pageNumber });

  render() {
    const { loading, video, project, authenticated, projects, ...rest } = this.props;
    const { showList, currentPage } = this.state;

    const skip = currentPage * ITEMS_PER_PAGE - ITEMS_PER_PAGE;
    const pageProjects = filter(
      projects,
      (projectItem, index) => index >= skip && index < skip + ITEMS_PER_PAGE
    );

    const childProps = {
      tablePaginationProps: {
        currentPage,
        isPaginationOn: true,
        itemsPerPage: ITEMS_PER_PAGE,
        totalCount: projects.length,
        changePage: this.changePage
      },
      emptyTableAlertProps: {
        message: `No Projects yet!`,
        numberOfColumns: 2
      }
    };

    const embedURL = videoOps.getURL(video).substr(-1, 12);
    const embedImageURL = `https://i3.ytimg.com/vi/${embedURL}/maxresdefault.jpg`;

    if (loading) return <Loading />;
    if (videoOps.isDeleted(video) || projectOps.isDeleted(project)) return <NotFound />;
    return (
      <>
        <SEO
          contentType="video"
          description={videoOps.getDescription(video)}
          images={{ twitter: embedImageURL }}
          path={authenticated ? `sharedPrivate/videos/${video._id}` : `shared/videos/${video._id}`}
          published={videoOps.getCreation(video)}
          title={videoOps.getName(video)}
          updated={videoOps.getLastUpdate(video)}
        />
        <ListModal
          childProps={childProps}
          items={pageProjects}
          show={showList}
          title="Projects"
          onHide={this.closeProjectList}
        >
          {map(pageProjects, pageProject => (
            <tr key={projectOps.getId(pageProject)}>
              <TableNameCell item={pageProject} itemList="" />
              <TableCopyCell
                itemID={projectOps.getId(pageProject)}
                itemList=""
                copyItem={projectId =>
                  this.copyVideoToProject(
                    videoOps.getId(video),
                    projectId,
                    projectOps.getName(pageProject)
                  )
                }
              />
            </tr>
          ))}
        </ListModal>
        <PageHeader>
          <h4>{videoOps.getName(video)}</h4>
          {authenticated && (
            <div className="ml-auto d-flex flex-row align-items-center justify-content-end">
              <Button
                className="ml-auto"
                size="sm"
                onClick={this.openProjectList}
                variant="outline-primary"
              >
                Copy to project
              </Button>
            </div>
          )}
        </PageHeader>
        <VideoVisualizerContainer video={video} {...rest} isPublic />
      </>
    );
  }
}

PublicVideo.defaultProps = {
  annotations: [],
  project: {},
  projects: [],
  video: {}
};

PublicVideo.propTypes = {
  annotations: PropTypes.array,
  authenticated: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  project: PropTypes.object,
  projects: PropTypes.array,
  video: PropTypes.object
};

export default compose(
  connect(state => ({ ...state })),
  withTracker(({ match }) => {
    const videoId = match.params.video_id;
    const videoSub = Meteor.isClient && Meteor.subscribe('videos.public', videoId);
    const video = Videos.findOne(videoId);
    const annotationsSub = Meteor.isClient && Meteor.subscribe('annotations.video', videoId);
    const annotations = Annotations.find({}, { sort: { videoTime: 1 } }).fetch();
    const projectSub = Meteor.isClient && Meteor.subscribe('projects.video', videoId, true);
    let projectId = '';
    if (!isNil(video)) projectId = videoOps.getProject(video);
    const project = Projects.findOne(projectId);
    const projects = Projects.find(
      { _id: { $ne: projectId }, deleted: { $eq: 'no' } },
      { sort: { name: 1 } }
    ).fetch();

    return {
      annotations,
      loading:
        !annotationsSub ||
        !annotationsSub.ready() ||
        !projectSub ||
        !projectSub.ready() ||
        !videoSub ||
        !videoSub.ready(),
      project,
      projects,
      video
    };
  })
)(PublicVideo);
